/**
 * `confirmDiscardScribeSession.ts` — shared "Discard captured transcript?"
 * confirm modal for the interrupted-visit recovery flows.
 *
 * The same Mantine confirm-modal shell (title, copy, red Discard / Keep
 * buttons) was duplicated near-verbatim in two places:
 *   - `useInterruptedVisitRecovery.discard` (the live-room recovery gate)
 *   - `CallSessionProvider.discardInterrupted` (the provider-level teardown)
 *
 * Only the post-confirm side effects differ between them (one proceeds to a
 * fresh start / exit, the other resolves a Promise after clearing provider
 * state), so we factor JUST the modal shell here and let each caller pass its
 * own `onConfirm` / `onCancel`.
 */
import { modals } from '@mantine/modals'

export type ConfirmDiscardScribeSessionArgs = {
  /** Number of captured transcript lines — drives the copy + pluralization. */
  lineCount: number
  /** Runs when the doctor confirms the discard (the destructive action). */
  onConfirm: () => void | Promise<void>
  /** Optional — runs when the doctor cancels / dismisses ("Keep"). */
  onCancel?: () => void
}

/**
 * Open the shared discard-confirm modal. The caller owns whatever happens in
 * `onConfirm` (delete the session, navigate, resolve a promise, …); this helper
 * only guarantees consistent copy and styling.
 */
export function confirmDiscardScribeSession({
  lineCount,
  onConfirm,
  onCancel,
}: ConfirmDiscardScribeSessionArgs): void {
  modals.openConfirmModal({
    title: 'Discard captured transcript?',
    centered: true,
    children: `Delete ${lineCount} captured ${lineCount === 1 ? 'line' : 'lines'}? This can't be undone.`,
    labels: { confirm: 'Discard', cancel: 'Keep' },
    confirmProps: { color: 'red' },
    onConfirm: () => void onConfirm(),
    onCancel,
  })
}
